
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Component, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, inject } from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { CommonUtil } from "../../../../common/utils/common.utils";
import { Router } from "@angular/router";
import { SuccessService } from "../../../../common/providers/success-service";
import { ErrorService } from "../../../../common/providers/error-service";
import { TaskRepository } from "../../../repository/task.repository";
import { ErrorType, SuccessType } from "../../../shared/task-enum";
import { Store } from "@ngrx/store";
import { TasksRootState } from "../../../store/reducers";
import { Broadcaster } from "../../../../common/providers/broadcaster.service";
import { getOnlineStatus } from "../../../../reducers";
import { takeWhile } from "rxjs/operators";
import { VncLibraryService, VNCSpinnerComponent } from "@uxf/vnc-library";
import { MatFormField, MatError } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { TranslatePipe } from "@ngx-translate/core";
import { StorageService } from "../../../../common/services/storage.service";

@Component({
    selector: "vp-registration-link-expired",
    templateUrl: "./registration-link-expired.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, ReactiveFormsModule, MatFormField, MatInput, MatError, VNCSpinnerComponent, TranslatePipe]
})
export class RegistrationLinkExpiredComponent implements OnDestroy {
  private changerDetectorRef = inject(ChangeDetectorRef);
  private fb = inject(UntypedFormBuilder);
  private router = inject(Router);
  private tasksRepo = inject(TaskRepository);
  private errorService = inject(ErrorService);
  private successService = inject(SuccessService);
  private vncLibaryService = inject(VncLibraryService);
  private store = inject<Store<TasksRootState>>(Store);
  private broadcaster = inject(Broadcaster);

  backgroundImage = (CommonUtil.isOnMobileDevice()) ? "url(" + CommonUtil.getFullUrl("/assets/img/profile-background.jpg") + " )" : "none";
  resendLinkForm: UntypedFormGroup;
  email: string = "";
  isAlive: boolean = true;
  isLoading: boolean = false;
  isOnline: boolean = false;
  constructor() {
    const fb = this.fb;

    this.handleResendLinkErrorMessages();
    this.handleResendLinkSuccessMessages();
    this.resendLinkForm = fb.group({
      "email": [null, Validators.compose([Validators.required])],
    });

    this.store.select(getOnlineStatus).pipe(takeWhile(() => this.isAlive)).subscribe((isOnline) => {
      this.isOnline = isOnline;
      this.changerDetectorRef.markForCheck();
    });

    this.broadcaster.on<any>("closeAllMdlDialogs").pipe(takeWhile(() => this.isAlive))
      .subscribe(presence => {
        this.returnToLoginPage();
      });

    this.broadcaster.on<any>("hideForgotPasswordFormPage").pipe(takeWhile(() => this.isAlive))
      .subscribe(presence => {
        this.returnToLoginPage();
      });

    this.tasksRepo.removeLoginIframe();
  }

  handleResendLinkErrorMessages() {
    this.errorService.only(ErrorType.ResendActivationLinkError).pipe(takeWhile(() => this.isAlive)).subscribe(error => {
      this.vncLibaryService.openSnackBar(error.messages, "","", "", 3000, "bottom", "left").subscribe(res => {
      });
      this.isLoading = false;
      this.changerDetectorRef.markForCheck();
    });
  }

  handleResendLinkSuccessMessages() {
    this.successService.only(SuccessType.ResendActivationLinkSuccess).pipe(takeWhile(() => this.isAlive)).subscribe(success => {
      this.isLoading = false;
      this.changerDetectorRef.markForCheck();
      StorageService.setLocalItem("registration_email", this.resendLinkForm.value.email);
      this.router.navigate(["/task/registration/activation"], { queryParams: {} });
    });
  }

  onFormSubmit(form) {
    if (this.isOnline) {
      this.isLoading = true;
      this.tasksRepo.resendActivationLink(form.email);
    } else {
      this.broadcaster.broadcast("OFFLINE_CONNECTION", true);
    }
  }

  returnToLoginPage() {
    this.router.navigate(["/task"], { queryParams: {} });
  }

  ngOnDestroy(): void {
    this.isAlive = false;
    this.changerDetectorRef.markForCheck();
  }
}
